<script setup lang="ts">
/**
 * Layout des Design-Editors – angelehnt an den normalen Editor:
 *   Werkzeugleiste | linke Spalte (Produkt-Konfigurator oder Werkzeug) | Kopfzeile + Bühne | Eigenschaften
 * Die Eigenschaften erscheinen nur, wenn ein Element ausgewählt ist.
 * Phone: Bühne voll, Werkzeugleiste als Bottom-Tabbar, Panels als Bottom-Sheet.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { GripHorizontal, X } from 'lucide-vue-next'
import { useBreakpoint } from '../../composables/useBreakpoint'
import ToolRail, { type ToolKey } from './ToolRail.vue'
import PropertiesPanel from './PropertiesPanel.vue'
import LayerPanel from './LayerPanel.vue'
import WarningsBar from './WarningsBar.vue'
import { useDesignStore } from '../../store/useDesignStore'
import type { DesignFontSummary } from '../../services/designApi'

const props = defineProps<{
  features: { text: boolean; stickers?: boolean; templates: boolean; collage: boolean; backgrounds: boolean; freeColor: boolean; designs?: boolean }
  fonts: DesignFontSummary[]
  palette: string[]
}>()
const emit = defineEmits<{ (e: 'replace-image', layerId: string): void }>()

const store = useDesignStore()
const phone = useBreakpoint('phone')
const activeTool = ref<ToolKey>('product')
const sheetOpen = ref(false)
const propsOpen = ref(false)

function selectTool(t: ToolKey) {
  if (phone.matches.value) {
    if (activeTool.value === t && sheetOpen.value) {
      sheetOpen.value = false
      return
    }
    activeTool.value = t
    sheetOpen.value = true
    propsOpen.value = false
    return
  }
  // Desktop: erneuter Klick auf ein Werkzeug führt zurück zum Produkt
  activeTool.value = activeTool.value === t && t !== 'product' ? 'product' : t
}
// Während eines Drags (Foto/Datei) das schwebende Eigenschaften-Panel durchlässig machen,
// sonst landet ein Drop darauf statt auf dem Motiv darunter.
const dragging = ref(false)
let dragDepth = 0
function onDocDragEnter() { dragDepth += 1; dragging.value = true }
function onDocDragLeave() { dragDepth = Math.max(0, dragDepth - 1); if (!dragDepth) dragging.value = false }
function onDocDragEnd() { dragDepth = 0; dragging.value = false }
// Datei neben ein Drop-Ziel fallen gelassen: nicht vom Browser öffnen lassen (würde den Editor verlassen)
function isFileDrag(e: DragEvent) { return Array.from(e.dataTransfer?.types ?? []).includes('Files') }
function onDocDragOver(e: DragEvent) { if (isFileDrag(e) && !e.defaultPrevented && e.dataTransfer) { e.preventDefault(); e.dataTransfer.dropEffect = 'none' } }
function onDocDrop(e: DragEvent) { if (isFileDrag(e)) e.preventDefault(); onDocDragEnd() }
onMounted(() => {
  document.addEventListener('dragenter', onDocDragEnter)
  document.addEventListener('dragleave', onDocDragLeave)
  document.addEventListener('dragend', onDocDragEnd)
  document.addEventListener('dragover', onDocDragOver)
  document.addEventListener('drop', onDocDrop)
})
onBeforeUnmount(() => {
  document.removeEventListener('dragenter', onDocDragEnter)
  document.removeEventListener('dragleave', onDocDragLeave)
  document.removeEventListener('dragend', onDocDragEnd)
  document.removeEventListener('dragover', onDocDragOver)
  document.removeEventListener('drop', onDocDrop)
})

/** Öffnet ein Werkzeug ohne Umschalt-Verhalten (für Aufrufe aus dem Code, z. B. Drop/„Bild ersetzen“). */
function openTool(t: ToolKey) {
  if (activeTool.value === t && (!phone.matches.value || sheetOpen.value)) return
  if (phone.matches.value) sheetOpen.value = false
  selectTool(t)
}
watch(() => store.selection.join(','), (sel) => {
  if (phone.matches.value && sel) { propsOpen.value = true; sheetOpen.value = false }
})
const panelTitle = computed(() => ({ product: 'Produkt', images: 'Bilder', text: 'Text', stickers: 'Sticker', templates: 'Vorlagen', layouts: 'Layouts', background: 'Hintergrund', layers: 'Ebenen', designs: 'Meine Designs' } as Record<string, string>)[activeTool.value] || '')
const showProps = computed(() => !phone.matches.value && !!store.selectedLayer)
/* ---------- Eigenschaften-Karte verschieben (Desktop/Tablet), Position wird gemerkt ---------- */
const PROPS_POS_KEY = 'pfe-design-props-pos'
const propsPos = ref<{ dx: number; dy: number }>({ dx: 0, dy: 0 })
try { const v = JSON.parse(localStorage.getItem(PROPS_POS_KEY) || 'null'); if (v && Number.isFinite(v.dx) && Number.isFinite(v.dy)) propsPos.value = v } catch { /* ohne Speicher: Standardposition */ }
const propsEl = ref<HTMLElement | null>(null)
let drag: { startX: number; startY: number; dx: number; dy: number; minDx: number; maxDx: number; minDy: number; maxDy: number } | null = null
function onPropsDragStart(e: PointerEvent) {
  const card = propsEl.value
  const stage = card?.parentElement
  if (!card || !stage) return
  e.preventDefault()
  ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  const c = card.getBoundingClientRect()
  const s = stage.getBoundingClientRect()
  const { dx, dy } = propsPos.value
  // Grenzen: Karte bleibt vollständig in der Bühne
  drag = { startX: e.clientX, startY: e.clientY, dx, dy, minDx: dx - (c.left - s.left), maxDx: dx + (s.right - c.right), minDy: dy - (c.top - s.top), maxDy: dy + (s.bottom - c.bottom) }
}
function onPropsDragMove(e: PointerEvent) {
  if (!drag) return
  const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v))
  propsPos.value = { dx: clamp(drag.dx + e.clientX - drag.startX, drag.minDx, drag.maxDx), dy: clamp(drag.dy + e.clientY - drag.startY, drag.minDy, drag.maxDy) }
}
function onPropsDragEnd() {
  if (!drag) return
  drag = null
  try { localStorage.setItem(PROPS_POS_KEY, JSON.stringify(propsPos.value)) } catch { /* egal */ }
}
function resetPropsPos() {
  propsPos.value = { dx: 0, dy: 0 }
  try { localStorage.removeItem(PROPS_POS_KEY) } catch { /* egal */ }
}
const propsStyle = computed(() => (propsPos.value.dx || propsPos.value.dy ? { transform: `translate(${propsPos.value.dx}px, ${propsPos.value.dy}px)` } : undefined))

defineExpose({ selectTool, openTool, activeTool })
</script>

<template>
  <div class="shell" :class="{ 'shell--phone': phone.matches.value, 'shell--dragging': dragging }">
    <ToolRail v-if="!phone.matches.value" class="shell__rail" :active="activeTool" :features="features" @select="selectTool" />

    <aside v-if="!phone.matches.value" class="shell__panel" :aria-label="panelTitle">
      <!-- Produkt-Konfigurator bleibt gemountet (Preis/Optionen behalten ihren Zustand) -->
      <div v-show="activeTool === 'product'" class="shell__product">
        <slot name="product" />
      </div>
      <template v-if="activeTool !== 'product'">
        <header class="shell__panel-head">
          <strong>{{ panelTitle }}</strong>
          <button type="button" class="shell__close" aria-label="Zurück zum Produkt" title="Zurück zum Produkt" @click="activeTool = 'product'"><X :size="16" /></button>
        </header>
        <div class="shell__panel-body">
          <LayerPanel v-if="activeTool === 'layers'" />
          <slot v-else :name="activeTool" />
        </div>
      </template>
    </aside>

    <main class="shell__main">
      <slot name="top" />
      <div class="shell__stage">
        <slot name="stage" />
        <!-- Schwebt über der Bühne: verändert deren Größe nicht (sonst springt die Fläche beim Anklicken/Ziehen) -->
        <aside v-if="showProps" ref="propsEl" class="shell__props" :style="propsStyle" aria-label="Eigenschaften">
          <div
            class="shell__props-grip"
            title="Ziehen zum Verschieben · Doppelklick: zurück an den Rand"
            @pointerdown="onPropsDragStart"
            @pointermove="onPropsDragMove"
            @pointerup="onPropsDragEnd"
            @pointercancel="onPropsDragEnd"
            @dblclick="resetPropsPos"
          ><GripHorizontal :size="16" aria-hidden="true" /></div>
          <button type="button" class="shell__close shell__props-close" aria-label="Auswahl aufheben" title="Auswahl aufheben" @click="store.select([])"><X :size="16" /></button>
          <PropertiesPanel :fonts="fonts" :palette="palette" :allow-free-color="features.freeColor" @replace-image="emit('replace-image', $event)" />
        </aside>
      </div>
      <WarningsBar />
    </main>

    <!-- Phone -->
    <template v-if="phone.matches.value">
      <div v-show="sheetOpen && activeTool === 'product'" class="sheet sheet--product" role="dialog" aria-label="Produkt">
        <header class="sheet__head"><strong>Produkt</strong><button type="button" class="shell__close" aria-label="Schließen" @click="sheetOpen = false"><X :size="16" /></button></header>
        <div class="sheet__body"><slot name="product" /></div>
      </div>
      <div v-if="sheetOpen && activeTool !== 'product'" class="sheet" role="dialog" :aria-label="panelTitle">
        <header class="sheet__head"><strong>{{ panelTitle }}</strong><button type="button" class="shell__close" aria-label="Schließen" @click="sheetOpen = false"><X :size="16" /></button></header>
        <div class="sheet__body">
          <LayerPanel v-if="activeTool === 'layers'" />
          <slot v-else :name="activeTool" />
        </div>
      </div>
      <div v-else-if="propsOpen && store.selectedLayer" class="sheet sheet--props" role="dialog" aria-label="Eigenschaften">
        <header class="sheet__head"><strong>Eigenschaften</strong><button type="button" class="shell__close" aria-label="Schließen" @click="propsOpen = false"><X :size="16" /></button></header>
        <div class="sheet__body"><PropertiesPanel :fonts="fonts" :palette="palette" :allow-free-color="features.freeColor" @replace-image="emit('replace-image', $event)" /></div>
      </div>
      <ToolRail class="shell__tabbar" :active="sheetOpen ? activeTool : null" :features="features" mobile @select="selectTool" />
    </template>
  </div>
</template>

<style scoped>
.shell--dragging .shell__props { pointer-events: none; opacity: 0.35; transition: opacity 0.12s; }
.shell { display: grid; grid-template-columns: 112px minmax(280px, 340px) minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); height: 100%; min-height: 0; background: #fff; }
.shell__rail { grid-column: 1; min-height: 0; }
.shell__panel { grid-column: 2; min-width: 0; min-height: 0; border-right: 1px solid rgba(15, 23, 42, 0.08); display: flex; flex-direction: column; background: #fff; }
.shell__product { flex: 1; min-height: 0; overflow: auto; }
.shell__panel-head, .sheet__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 14px 16px; border-bottom: 1px solid rgba(15, 23, 42, 0.08); font-size: 15px; }
.shell__panel-body, .sheet__body { overflow: auto; flex: 1; min-height: 0; }
.shell__main { grid-column: 3; display: flex; flex-direction: column; min-width: 0; min-height: 0; background: #f1f3f6; container-type: inline-size; container-name: dmain; }
.shell__stage { position: relative; flex: 1; min-height: 0; display: flex; flex-direction: column; }
.shell__stage > :first-child { flex: 1; min-height: 0; }
.shell__props { position: absolute; top: 12px; right: 12px; bottom: 64px; z-index: 5; width: 300px; max-width: calc(100% - 24px); overflow-x: hidden; overflow-y: auto; background: #fff; border: 1px solid rgba(15, 23, 42, 0.1); border-radius: 10px; box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16); overscroll-behavior: contain; }
.shell__props-close { position: absolute; top: 14px; right: 8px; z-index: 2; }
.shell__props-grip { position: sticky; top: 0; z-index: 1; display: flex; justify-content: center; height: 16px; margin-bottom: -6px; padding-top: 2px; background: #fff; color: rgba(15, 23, 42, 0.35); cursor: grab; touch-action: none; }
.shell__props-grip:hover { color: rgba(15, 23, 42, 0.7); }
.shell__props-grip:active { cursor: grabbing; }
.shell__close { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 0; border-radius: 6px; background: transparent; color: rgba(15, 23, 42, 0.62); cursor: pointer; }
.shell__close:hover { background: rgba(15, 23, 42, 0.06); color: #0f172a; }

.shell--phone { grid-template-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1fr) auto; }
.shell--phone .shell__main { grid-column: 1; grid-row: 1; }
.shell__tabbar { grid-row: 2; }
.sheet { position: fixed; left: 0; right: 0; bottom: 0; max-height: 70vh; max-height: 70dvh; background: #fff; border-radius: 14px 14px 0 0; box-shadow: 0 -8px 30px rgba(15, 23, 42, 0.25); display: flex; flex-direction: column; z-index: 30; padding-bottom: env(safe-area-inset-bottom); }
.sheet--product { max-height: 85vh; max-height: 85dvh; }

@media (max-width: 1180px) {
  .shell__props { width: 270px; }
  .shell:not(.shell--phone) { grid-template-columns: 96px minmax(260px, 300px) minmax(0, 1fr); }
  .shell__rail { width: 96px; padding-left: 8px; padding-right: 8px; }
}
</style>
